import { useEffect, useState } from "react";
import { KeyRound, Loader2, PlugZap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const GROUPS = [
  {
    key: "payu",
    title: "PayU (płatności)",
    fields: [
      { k: "env", label: "Środowisko", type: "select", options: [["production", "Produkcja"], ["sandbox", "Sandbox (testowe)"]] },
      { k: "pos_id", label: "POS ID" },
      { k: "client_id", label: "Client ID", secret: true },
      { k: "client_secret", label: "Client Secret", secret: true },
      { k: "second_key", label: "Drugi klucz (Signature key)", secret: true },
    ],
  },
  {
    key: "gus",
    title: "GUS REGON (dane firm)",
    fields: [
      { k: "env", label: "Środowisko", type: "select", options: [["prod", "Produkcja"], ["test", "Testowe"]] },
      { k: "api_key", label: "Klucz API GUS", secret: true },
    ],
  },
  {
    key: "turnstile",
    title: "Cloudflare Turnstile (anty-bot)",
    fields: [
      { k: "site_key", label: "Site key (publiczny)" },
      { k: "secret_key", label: "Secret key", secret: true },
    ],
  },
  {
    key: "google",
    title: "Google Places API (opinie Google)",
    fields: [
      { k: "places_api_key", label: "Klucz API Google Places", secret: true },
    ],
  },
];

function GroupCard({ group, data, onSaved }) {
  const meta = data[group.key] || {};
  const [values, setValues] = useState(() => {
    const v = {};
    group.fields.forEach((f) => { if (!f.secret) v[f.k] = meta[f.k]?.value || meta[f.k]?.env_value || ""; });
    return v;
  });
  const [secretInputs, setSecretInputs] = useState({});
  const [clears, setClears] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payloadValues = { ...values };
      group.fields.forEach((f) => {
        if (f.secret && secretInputs[f.k]) payloadValues[f.k] = secretInputs[f.k];
      });
      const clearList = Object.keys(clears).filter((k) => clears[k]);
      const { data: fresh } = await api.put("/admin/integrations", { group: group.key, values: payloadValues, clears: clearList });
      setSecretInputs({});
      setClears({});
      onSaved(fresh);
      toast.success(`Zapisano: ${group.title}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się zapisać.");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data: r } = await api.post(`/admin/integrations/test/${group.key}`);
      r.ok ? toast.success(r.detail) : toast.error(r.detail);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test nie powiódł się.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div data-testid={`integration-card-${group.key}`} className="bg-card border border-border rounded-2xl p-6">
      <h3 className="font-display font-bold text-base flex items-center gap-2"><KeyRound size={16} className="text-brand-teal" /> {group.title}</h3>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {group.fields.map((f) => {
          const fm = meta[f.k] || {};
          if (f.type === "select") {
            return (
              <div key={f.k}>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{f.label}</label>
                <select
                  data-testid={`integration-${group.key}-${f.k}`}
                  value={values[f.k] ?? ""}
                  onChange={(e) => setValues({ ...values, [f.k]: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm"
                >
                  {f.options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                </select>
              </div>
            );
          }
          if (f.secret) {
            const status = fm.db_set ? "zapisany w bazie (szyfrowany)" : fm.env_set ? "używany z .env" : "brak";
            return (
              <div key={f.k}>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block flex items-center gap-1.5">
                  {f.label} <ShieldCheck size={12} className="text-brand-teal" />
                </label>
                <Input
                  data-testid={`integration-${group.key}-${f.k}`}
                  type="password"
                  autoComplete="new-password"
                  value={secretInputs[f.k] || ""}
                  disabled={!!clears[f.k]}
                  onChange={(e) => setSecretInputs({ ...secretInputs, [f.k]: e.target.value })}
                  placeholder={`•••••••• (${status})`}
                  className="bg-background"
                />
                <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                  <input
                    data-testid={`integration-${group.key}-${f.k}-clear`}
                    type="checkbox"
                    checked={!!clears[f.k]}
                    onChange={(e) => setClears({ ...clears, [f.k]: e.target.checked })}
                  />
                  Wyczyść z bazy (wróć do wartości z .env)
                </label>
              </div>
            );
          }
          return (
            <div key={f.k}>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{f.label}</label>
              <Input
                data-testid={`integration-${group.key}-${f.k}`}
                value={values[f.k] ?? ""}
                onChange={(e) => setValues({ ...values, [f.k]: e.target.value })}
                placeholder={fm.env_value ? `z .env: ${fm.env_value}` : ""}
                className="bg-background"
              />
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button data-testid={`integration-save-${group.key}`} onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-brand-teal text-white hover:bg-brand-tealDark disabled:opacity-60 font-semibold px-5 py-2.5 rounded-full text-sm transition-colors">
          {saving && <Loader2 size={14} className="animate-spin" />} Zapisz
        </button>
        <button data-testid={`integration-test-${group.key}`} onClick={test} disabled={testing} className="inline-flex items-center gap-2 border border-border hover:bg-muted disabled:opacity-60 font-semibold px-5 py-2.5 rounded-full text-sm transition-colors">
          {testing ? <Loader2 size={14} className="animate-spin" /> : <PlugZap size={14} />} Testuj
        </button>
      </div>
    </div>
  );
}

export function IntegrationsAdmin() {
  const [data, setData] = useState(null);
  const [testingAll, setTestingAll] = useState(false);

  useEffect(() => { api.get("/admin/integrations").then((r) => setData(r.data)).catch(() => setData({})); }, []);

  const testAll = async () => {
    setTestingAll(true);
    try {
      const { data: r } = await api.post("/admin/integrations/test-all");
      (r.results || []).forEach((res) => { res.ok ? toast.success(`${res.label}: ${res.detail}`) : toast.error(`${res.label}: ${res.detail}`); });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się sprawdzić integracji.");
    } finally {
      setTestingAll(false);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;

  return (
    <div className="space-y-5">
      <div className="bg-brand-teal/10 border border-brand-teal/30 rounded-2xl p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-sm text-foreground">
            <p className="font-semibold flex items-center gap-2"><ShieldCheck size={15} className="text-brand-teal" /> Klucze API — bezpieczne przechowywanie</p>
            <p className="text-muted-foreground mt-1 leading-relaxed text-xs max-w-2xl">
              Sekrety są szyfrowane w bazie (AES/Fernet) i nigdy nie są wyświetlane po zapisaniu. Puste pole = używana jest wartość z pliku serwera (.env).
              Zmiana kluczy działa od razu dla nowych żądań; przy wielu procesach backendu zalecany restart. Konto pocztowe (SMTP) ma osobną zakładkę „Konto pocztowe".
            </p>
          </div>
          <button data-testid="integration-test-all" onClick={testAll} disabled={testingAll} className="shrink-0 inline-flex items-center gap-2 bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-60 font-semibold px-5 py-2.5 rounded-full text-sm transition-colors">
            {testingAll ? <Loader2 size={14} className="animate-spin" /> : <PlugZap size={14} />} Sprawdź wszystkie integracje
          </button>
        </div>
      </div>
      {GROUPS.map((g) => <GroupCard key={g.key} group={g} data={data} onSaved={setData} />)}
    </div>
  );
}
